import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import {
  useClaimScreenPairing,
  useCreatePiImageLink,
  useCreateScreen,
  useDashboards,
  usePiImage,
  useScreens,
  useSendScreenCommand,
  useTvClientPackage,
  useUpdateAllScreens
} from '../../api/queries'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'
import { config } from '../../config'
import { buildNetworkConfig, validateWifi } from '../../lib/piNetworkConfig'
import { formatDateTime, weekdayNames } from '../../lib/format'
import type { Screen, ScreenSchedule } from '../../api/types'

const commands = [
  { type: 'reload_dashboard', label: 'Genindlæs dashboard' },
  { type: 'restart_browser', label: 'Genstart browser' },
  { type: 'restart_device', label: 'Genstart Pi' },
  { type: 'tv_on', label: 'Tænd TV' },
  { type: 'tv_off', label: 'Sluk TV' },
  { type: 'reload_config', label: 'Genindlæs konfiguration' },
  { type: 'show_test_page', label: 'Vis testside' }
]

/** Administration af TV-skærme: oprettelse, tokens, kommandoer og ugeplan (kravspec afsnit 19-25). */
export function ScreensPage() {
  const { data, isLoading, error } = useScreens()
  const [showForm, setShowForm] = useState(false)
  const [showPair, setShowPair] = useState(false)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between">
        <p className="small muted" style={{ margin: 0 }}>
          Hver skærm har sit eget device token. En ny Raspberry Pi med vores image viser en kode på TV&apos;et -
          tryk Tilknyt med kode.
        </p>
        <div className="btn-group">
          <button className="btn btn--primary" onClick={() => { setShowPair((v) => !v); setShowForm(false) }}>
            {showPair ? 'Luk' : 'Tilknyt med kode'}
          </button>
          <button className="btn" onClick={() => { setShowForm((v) => !v); setShowPair(false) }}>
            {showForm ? 'Luk' : 'Ny skærm med token'}
          </button>
        </div>
      </div>

      {showPair && <PairScreenForm onDone={() => setShowPair(false)} />}
      {showForm && <NewScreenForm onDone={() => setShowForm(false)} />}

      <ClientPackageCard />
      <PiImageCard />

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && data.length === 0 && <EmptyState>Der er ikke oprettet nogen skærme endnu.</EmptyState>}

      {data?.map((screen) => (
        <ScreenCard key={screen.id} screen={screen} />
      ))}
    </div>
  )
}

/**
 * Den TV-klient, serveren har klar (beslutning 074).
 *
 * Pakken laves ved hver udrulning. Opdateringen sendes med vilje ikke af sig selv:
 * en fejl i en ny version skal ramme de skaerme, man selv har valgt - ikke alle
 * paa én gang. Og Pi'en ruller selv tilbage, hvis den nye version ikke melder sig.
 */
function ClientPackageCard() {
  const { data } = useTvClientPackage()
  const updateAll = useUpdateAllScreens()
  const [feedback, setFeedback] = useState<string | null>(null)

  if (!data) return null

  async function sendToAll() {
    setFeedback(null)
    try {
      const result = await updateAll.mutateAsync()
      setFeedback(
        result.sent === 0
          ? 'Ingen skærme var bagud.'
          : `Version ${result.version} er sendt til ${result.sent === 1 ? '1 skærm' : `${result.sent} skærme`}.`
      )
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Opdateringen kunne ikke sendes.')
    }
  }

  return (
    <Card title="TV-klient">
      {data.available ? (
        <>
          <div className="small">
            Serveren har version <strong>{data.version}</strong> klar
            {data.builtAt ? `, lagt op ${formatDateTime(data.builtAt)}` : ''}.
          </div>
          <div className="tiny muted" style={{ marginTop: 4 }}>
            Pakken laves ved hver udrulning. En skærm, der opdateres, henter den, kontrollerer den og
            skifter over - og ruller selv tilbage, hvis den nye version ikke melder sig inden for 3
            minutter. En skærm, der er offline, får kommandoen, når den melder sig igen - hvis det
            sker inden for en time.
          </div>
          <div className="row" style={{ gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn btn--sm"
              disabled={updateAll.isPending || data.screensBehind === 0}
              onClick={() => void sendToAll()}
            >
              {data.screensBehind === 0
                ? 'Alle skærme kører den nyeste'
                : data.screensBehind === 1
                  ? 'Opdater 1 skærm, der er bagud'
                  : `Opdater ${data.screensBehind} skærme, der er bagud`}
            </button>
          </div>
        </>
      ) : (
        <div className="small muted">
          Der ligger ingen TV-klientpakke på serveren. Den laves af udrulningen (deploy.sh).
        </div>
      )}
      {feedback && <div className="small muted" style={{ marginTop: 8 }}>{feedback}</div>}
    </Card>
  )
}

/**
 * Raspberry Pi-imaget til nye skaerme (beslutning 075).
 *
 * Imaget bygges paa serveren efter hver udrulning og er det samme for alle skaerme:
 * ingen token, ingen adgangskoder. En ny skaerm viser en kode paa TV'et, og den
 * tastes under "Tilknyt med kode".
 */
function PiImageCard() {
  const { data } = usePiImage()
  const createLink = useCreatePiImageLink()
  const [error, setError] = useState<string | null>(null)
  const [showWifi, setShowWifi] = useState(false)

  if (!data) return null

  async function download() {
    setError(null)
    try {
      const link = await createLink.mutateAsync()
      // Browseren henter selv filen med linket - saa ligger 500 MB ikke i hukommelsen.
      window.location.assign(`${config.apiUrl}${link.url}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Linket kunne ikke laves.')
    }
  }

  return (
    <Card title="Raspberry Pi-image til nye skærme">
      {data.buildState === 'building' && (
        <div className="small" style={{ marginBottom: 8 }}>
          Et nyt image bygges på serveren
          {data.buildStartedAt ? ` (startet ${formatDateTime(data.buildStartedAt)})` : ''}. Det tager 10-20 minutter.
        </div>
      )}
      {data.buildState === 'failed' && (
        <div className="error-box" style={{ marginBottom: 8 }}>
          Seneste bygning af imaget fejlede{data.buildMessage ? `: ${data.buildMessage}` : '.'}
          {data.available ? ' Det forrige image kan stadig hentes.' : ''}
        </div>
      )}

      {data.available ? (
        <>
          <div className="small">
            Raspberry Pi OS med TV-klient <strong>{data.clientVersion}</strong>, bygget{' '}
            {formatDateTime(data.builtAt)} - {Math.round((data.size ?? 0) / 1024 / 1024)} MB.
          </div>
          <div className="tiny muted" style={{ marginTop: 4, wordBreak: 'break-all' }}>
            {data.fileName} - SHA-256 {data.sha256}
          </div>

          <div className="btn-group" style={{ marginTop: 10 }}>
            <button className="btn btn--primary btn--sm" disabled={createLink.isPending} onClick={() => void download()}>
              Hent image
            </button>
            <button className="btn btn--sm" onClick={() => setShowWifi((v) => !v)}>
              {showWifi ? 'Skjul Wi-Fi' : 'Lav Wi-Fi-fil'}
            </button>
          </div>

          <ol className="small" style={{ margin: '12px 0 0', paddingLeft: 20, lineHeight: 1.6 }}>
            <li>
              Skriv imaget på et SD-kort med <strong>Raspberry Pi Imager</strong> (vælg &quot;Use custom&quot;). Spring
              tilpasning af indstillinger over - ellers overskriver Imager opsætningen.
            </li>
            <li>
              Kabel: der er intet at gøre. Wi-Fi: lav Wi-Fi-filen her og læg den på kortets <code>bootfs</code>-drev i
              stedet for <code>network-config</code>, før Pi&apos;en starter første gang.
            </li>
            <li>
              Sæt kortet i, forbind HDMI til TV&apos;et og sæt strøm til. Logoet kommer frem med det samme, og første
              opstart tager et par minutter.
            </li>
            <li>
              Når TV&apos;et viser en kode på 6 cifre, tryk <strong>Tilknyt med kode</strong> øverst på siden.
            </li>
          </ol>
        </>
      ) : (
        data.buildState !== 'building' && (
          <div className="small muted">
            Der ligger intet image på serveren endnu. Det bygges automatisk efter en udrulning.
          </div>
        )
      )}

      {error && <div className="error-box" style={{ marginTop: 10 }}>{error}</div>}
      {showWifi && data.available && <WifiFileForm />}
    </Card>
  )
}

/** Laver network-config med Wi-Fi i browseren. Adgangskoden sendes ikke til serveren. */
function WifiFileForm() {
  const [ssid, setSsid] = useState('')
  const [password, setPassword] = useState('')
  const [hidden, setHidden] = useState(false)
  const problem = validateWifi(ssid, password)

  function save(event: React.FormEvent) {
    event.preventDefault()
    if (problem) return

    const blob = new Blob([buildNetworkConfig({ ssid, password, hidden })], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    // Uden endelse: cloud-init leder efter praecis det navn.
    anchor.download = 'network-config'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <form onSubmit={save} style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <p className="small muted" style={{ marginTop: 0 }}>
        Filen laves her i browseren - adgangskoden sendes ikke til serveren. Den står til gengæld i filen på SD-kortet,
        så kortet skal behandles som en nøgle til netværket.
      </p>
      <div className="form-grid">
        <label className="field">
          <span className="field__label">Netværkets navn (SSID) *</span>
          <input required autoComplete="off" value={ssid} onChange={(e) => setSsid(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Adgangskode</span>
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Tom ved et åbent netværk"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} style={{ width: 'auto' }} />
          <span className="small">Netværket er skjult</span>
        </label>
      </div>
      {ssid && problem && <div className="small warn" style={{ marginBottom: 8 }}>{problem}</div>}
      <button className="btn btn--primary btn--sm" type="submit" disabled={Boolean(problem)}>
        Hent network-config
      </button>
    </form>
  )
}

/**
 * Tilknyt en ny Raspberry Pi med koden paa TV'et (beslutning 075).
 *
 * Imaget er det samme for alle skaerme og har intet token. Foerste gang Pi'en starter,
 * viser TV'et en kode - og at taste den her beviser, at man staar foran netop det TV.
 */
function PairScreenForm({ onDone }: { onDone: () => void }) {
  const claim = useClaimScreenPairing()
  const { data: dashboards } = useDashboards()
  const { data: screens } = useScreens()
  const [code, setCode] = useState('')
  const [target, setTarget] = useState('')
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [dashboardId, setDashboardId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const screen = await claim.mutateAsync({
        code,
        screenId: target ? Number(target) : null,
        name: target ? null : name,
        location: target ? null : location || null,
        dashboardId: target ? null : dashboardId ? Number(dashboardId) : null
      })
      setDone(screen.name)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Skærmen kunne ikke tilknyttes.')
    }
  }

  if (done) {
    return (
      <Card title="Skærmen er tilknyttet">
        <p className="small">
          <strong>{done}</strong> henter sit token inden for få sekunder og skifter selv over til
          dashboardet. Der er intet token at kopiere.
        </p>
        <button className="btn btn--primary" onClick={onDone}>
          Luk
        </button>
      </Card>
    )
  }

  return (
    <Card title="Tilknyt skærm med kode">
      <p className="small muted" style={{ marginTop: 0 }}>
        Første gang en Raspberry Pi med vores image starter, viser TV&apos;et en kode på 6 cifre.
        Skriv den her. Koden virker i et kvarter - genstart Pi&apos;en for at få en ny.
      </p>
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Koden fra TV&apos;et *</span>
            <input
              required
              inputMode="numeric"
              autoComplete="off"
              placeholder="123 456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ fontSize: 22, letterSpacing: 4 }}
            />
          </label>
          <label className="field">
            <span className="field__label">Tilknyt til</span>
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">En ny skærm</option>
              {screens?.map((screen) => (
                <option key={screen.id} value={screen.id}>
                  {screen.name} (erstat Pi&apos;en)
                </option>
              ))}
            </select>
          </label>
          {!target && (
            <>
              <label className="field">
                <span className="field__label">Navn *</span>
                <input required placeholder="Kontor TV" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field">
                <span className="field__label">Placering</span>
                <input value={location} onChange={(e) => setLocation(e.target.value)} />
              </label>
              <label className="field">
                <span className="field__label">Dashboard</span>
                <select value={dashboardId} onChange={(e) => setDashboardId(e.target.value)}>
                  <option value="">Standarddashboard</option>
                  {dashboards?.map((dashboard) => (
                    <option key={dashboard.id} value={dashboard.id}>
                      {dashboard.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>

        {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={claim.isPending}>
            Tilknyt
          </button>
          <button className="btn" type="button" onClick={onDone}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

function NewScreenForm({ onDone }: { onDone: () => void }) {
  const createScreen = useCreateScreen()
  const { data: dashboards } = useDashboards()
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [dashboardId, setDashboardId] = useState('')
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const result = await createScreen.mutateAsync({
        name,
        location: location || null,
        dashboardId: dashboardId ? Number(dashboardId) : null
      })
      setToken(result.deviceToken)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke oprette skærmen.')
    }
  }

  if (token) {
    return (
      <Card title="Skærmen er oprettet">
        <p className="small">
          Kopiér tokenet nu - det kan ikke vises igen. Sæt det ind i Raspberry Pi-klientens
          konfigurationsfil <code>/etc/tcpss-tv/tv-client.env</code> som <code>DEVICE_TOKEN</code>.
        </p>
        <textarea readOnly value={token} style={{ fontFamily: 'monospace', minHeight: 60 }} />
        <button className="btn btn--primary" style={{ marginTop: 10 }} onClick={onDone}>
          Jeg har gemt tokenet
        </button>
      </Card>
    )
  }

  return (
    <Card title="Ny TV-skærm">
      <form onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input required placeholder="Kontor TV" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Placering</span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Dashboard</span>
            <select value={dashboardId} onChange={(e) => setDashboardId(e.target.value)}>
              <option value="">Standarddashboard</option>
              {dashboards?.map((dashboard) => (
                <option key={dashboard.id} value={dashboard.id}>
                  {dashboard.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" type="submit" disabled={createScreen.isPending}>
            Opret skærm
          </button>
          <button className="btn" type="button" onClick={onDone}>
            Fortryd
          </button>
        </div>
      </form>
    </Card>
  )
}

function ScreenCard({ screen }: { screen: Screen }) {
  const sendCommand = useSendScreenCommand()
  const { data: clientPackage } = useTvClientPackage()
  const queryClient = useQueryClient()
  const { data: dashboards } = useDashboards()
  const [message, setMessage] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)
  const [showSchedule, setShowSchedule] = useState(false)

  async function run(type: string, text?: string) {
    setFeedback(null)
    try {
      await sendCommand.mutateAsync({ screenId: screen.id, type, message: text })
      setFeedback(
        type === 'update_client'
          ? 'Opdateringen er sendt. Skærmen henter den ved næste heartbeat og kvitterer, når den nye version er oppe - eller når den har rullet tilbage.'
          : `Kommandoen "${type}" er sendt.`
      )
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Kommandoen kunne ikke sendes.')
    }
  }

  // API'et kunne slette en skaerm, men der var aldrig en knap til det.
  async function remove() {
    const warning =
      `Slet skærmen "${screen.name}"?\n\n` +
      'Dens tokens, tidsplan og kommandoer slettes med den, og Pi\'en holder op med at vise dashboardet. ' +
      'En Pi fra vores image viser derefter en ny kode, så den kan tilknyttes igen.'
    if (!window.confirm(warning)) return

    setFeedback(null)
    try {
      await api.delete(`/api/screens/${screen.id}`)
      await queryClient.invalidateQueries({ queryKey: ['screens'] })
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Skærmen kunne ikke slettes.')
    }
  }

  async function changeDashboard(dashboardId: string) {
    await api.put(`/api/screens/${screen.id}`, { dashboardId: Number(dashboardId) })
    await queryClient.invalidateQueries({ queryKey: ['screens'] })
  }

  return (
    <Card
      title={screen.name}
      actions={
        <span className="pill" style={{ background: screen.isOnline ? '#DCFCE7' : '#FEE2E2', color: screen.isOnline ? '#166534' : '#991B1B' }}>
          <span className="pill__dot" />
          {screen.isOnline ? 'Online' : 'Offline'}
        </span>
      }
    >
      <div className="form-grid">
        <div>
          <div className="tiny muted">Enheds-id</div>
          <div className="small">{screen.deviceId}</div>
        </div>
        <div>
          <div className="tiny muted">Sidst set</div>
          <div className="small">{formatDateTime(screen.lastSeenAt)}</div>
        </div>
        <div>
          <div className="tiny muted">IP-adresse</div>
          <div className="small">{screen.lastIpAddress ?? '-'}</div>
        </div>
        <div>
          <div className="tiny muted">Klientversion</div>
          <div className="small">
            {screen.clientVersion ?? '-'}
            {screen.updateAvailable && clientPackage?.version && (
              <span className="warn"> - {clientPackage.version} ligger klar</span>
            )}
          </div>
        </div>
        <div>
          <div className="tiny muted">TV-tilstand</div>
          <div className="small">{screen.tvPowerState ?? 'ukendt'}</div>
        </div>
        <label className="field">
          <span className="field__label">Dashboard</span>
          <select value={screen.dashboardId ?? ''} onChange={(e) => void changeDashboard(e.target.value)}>
            {dashboards?.map((dashboard) => (
              <option key={dashboard.id} value={dashboard.id}>
                {dashboard.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {screen.scheduleOverrideState && (
        <div className="small warn" style={{ marginTop: 8 }}>
          Manuel overstyring: {screen.scheduleOverrideState === 'on' ? 'tændt' : 'slukket'}
          {screen.scheduleOverrideUntil ? ` indtil ${formatDateTime(screen.scheduleOverrideUntil)}` : ''}
          <button
            className="btn btn--sm"
            style={{ marginLeft: 8 }}
            onClick={async () => {
              await api.delete(`/api/screens/${screen.id}/override`)
              await queryClient.invalidateQueries({ queryKey: ['screens'] })
            }}
          >
            Fjern
          </button>
        </div>
      )}

      <div className="btn-group" style={{ marginTop: 14 }}>
        {commands.map((command) => (
          <button key={command.type} className="btn btn--sm" onClick={() => void run(command.type)}>
            {command.label}
          </button>
        ))}
        {screen.updateAvailable && clientPackage?.available && (
          <button className="btn btn--sm btn--primary" onClick={() => void run('update_client')}>
            Opdater til {clientPackage.version}
          </button>
        )}
        <button className="btn btn--sm" onClick={() => setShowSchedule((v) => !v)}>
          {showSchedule ? 'Skjul tidsplan' : 'Tidsplan'}
        </button>
        <button className="btn btn--sm btn--danger" onClick={() => void remove()}>
          Slet skærm
        </button>
      </div>

      <form
        className="row"
        style={{ marginTop: 10 }}
        onSubmit={(event) => {
          event.preventDefault()
          if (message.trim()) {
            void run('show_message', message.trim())
            setMessage('')
          }
        }}
      >
        <input placeholder="Vis besked på skærmen ..." value={message} onChange={(e) => setMessage(e.target.value)} />
        <button className="btn btn--sm" type="submit" disabled={!message.trim()}>
          Send besked
        </button>
      </form>

      {feedback && <div className="small muted" style={{ marginTop: 8 }}>{feedback}</div>}

      {showSchedule && <ScheduleEditor screen={screen} />}
    </Card>
  )
}

function ScheduleEditor({ screen }: { screen: Screen }) {
  const queryClient = useQueryClient()
  const [days, setDays] = useState<ScreenSchedule[]>(() =>
    Array.from({ length: 7 }, (_, dayOfWeek) =>
      screen.schedules.find((s) => s.dayOfWeek === dayOfWeek) ?? {
        dayOfWeek,
        onTime: '06:30',
        offTime: '17:00',
        isActive: dayOfWeek >= 1 && dayOfWeek <= 5
      }
    )
  )
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function update(dayOfWeek: number, changes: Partial<ScreenSchedule>) {
    setDays((current) => current.map((day) => (day.dayOfWeek === dayOfWeek ? { ...day, ...changes } : day)))
    setSaved(false)
  }

  async function save() {
    setError(null)
    try {
      await api.put(`/api/screens/${screen.id}/schedule`, { days })
      await queryClient.invalidateQueries({ queryKey: ['screens'] })
      setSaved(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme tidsplanen.')
    }
  }

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Ugedag</th>
              <th>Tænd</th>
              <th>Sluk</th>
              <th>Aktiv</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.dayOfWeek}>
                <td>{weekdayNames[day.dayOfWeek]}</td>
                <td style={{ width: 130 }}>
                  <input type="time" value={day.onTime} onChange={(e) => update(day.dayOfWeek, { onTime: e.target.value })} />
                </td>
                <td style={{ width: 130 }}>
                  <input type="time" value={day.offTime} onChange={(e) => update(day.dayOfWeek, { offTime: e.target.value })} />
                </td>
                <td>
                  <button
                    className={`btn btn--sm ${day.isActive ? 'btn--primary' : ''}`}
                    onClick={() => update(day.dayOfWeek, { isActive: !day.isActive })}
                  >
                    {day.isActive ? 'Tændt' : 'Slukket'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div className="error-box" style={{ marginTop: 10 }}>{error}</div>}

      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn btn--primary btn--sm" onClick={() => void save()}>
          Gem tidsplan
        </button>
        {saved && <span className="small muted">Gemt - klienten henter den nye tidsplan.</span>}
      </div>
    </div>
  )
}
